// IncomingCallOverlay — polls /calls/incoming every 4 s. When a ringing
// invitation targets the current user, shows a fullscreen Snapchat-style
// incoming-call modal with a continuous ring tone + Accept (green) / Decline
// (red) buttons. Accept navigates to /dm/{thread_id}?call=1.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, PhoneOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { getRingContext } from "../lib/ringAudio";

export default function IncomingCallOverlay() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [inv, setInv] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const ringIvRef = useRef(null);

  // Poll for incoming calls every 4 s when logged in
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/calls/incoming");
        if (!alive) return;
        // Ignore any invitation we've already dismissed locally
        const fresh = (data.items || []).find((i) => !dismissed.has(i.id));
        // Also suppress the ring when we're already inside the same thread's call page
        if (fresh && loc.pathname === `/dm/${fresh.thread_id}` && loc.search.includes("call=1")) return;
        setInv(fresh || null);
      } catch { /* silent */ }
    };
    poll();
    const iv = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [user, dismissed, loc.pathname, loc.search]);

  // Start / stop dual-tone ringtone based on `inv`. Uses the shared,
  // gesture-primed AudioContext (see lib/ringAudio) instead of creating a
  // fresh one here — a brand-new context created from this poll-driven
  // effect (no preceding click) starts suspended on most mobile browsers
  // and never actually makes a sound.
  useEffect(() => {
    if (ringIvRef.current) { clearInterval(ringIvRef.current); ringIvRef.current = null; }
    if (!inv) return;
    const ctx = getRingContext();
    if (!ctx) return;
    const gain = ctx.createGain(); gain.gain.value = 0.35; gain.connect(ctx.destination);
    const ringOnce = () => {
      const o1 = ctx.createOscillator(); o1.frequency.value = 440; o1.connect(gain); o1.start(); o1.stop(ctx.currentTime + 1);
      const o2 = ctx.createOscillator(); o2.frequency.value = 480; o2.connect(gain); o2.start(); o2.stop(ctx.currentTime + 1);
    };
    ringOnce();
    ringIvRef.current = setInterval(ringOnce, 3000);
    return () => {
      if (ringIvRef.current) clearInterval(ringIvRef.current);
    };
  }, [inv]);

  if (!inv) return null;

  const accept = async () => {
    try {
      await api.post("/calls/accept", { invitation_id: inv.id });
      setDismissed((s) => { const n = new Set(s); n.add(inv.id); return n; });
      setInv(null);
      nav(`/dm/${inv.thread_id}?call=1`);
    } catch { /* silent */ }
  };
  const decline = async () => {
    try { await api.post("/calls/decline", { invitation_id: inv.id }); } catch {}
    setDismissed((s) => { const n = new Set(s); n.add(inv.id); return n; });
    setInv(null);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-6" data-testid="incoming-call-overlay">
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-nexus-cyan animate-pulse">// incoming call</div>
        {inv.caller_avatar && (
          <img src={inv.caller_avatar} alt="" className="w-32 h-32 rounded-full border-4 border-nexus-cyan shadow-2xl animate-pulse" data-testid="incoming-call-avatar" />
        )}
        <div className="font-display font-black text-4xl text-white" data-testid="incoming-call-name">{inv.caller_name}</div>
        <div className="text-white/50 text-sm">is calling you…</div>
        <div className="flex gap-6 mt-4">
          <button
            onClick={decline}
            title="Decline"
            data-testid="incoming-call-decline"
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-2xl transition-transform hover:scale-105"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
          <button
            onClick={accept}
            title="Accept"
            data-testid="incoming-call-accept"
            className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-2xl transition-transform hover:scale-105 animate-pulse"
          >
            <Phone className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
